import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { WINDOW } from '@core/http/window.providers';
import { Observable, Subject, Subscription, fromEvent, interval } from 'rxjs';
import { debounce } from 'rxjs/operators';

@Component({
    selector: 'app-filter-sidebar',
    templateUrl: './filter.component.html',
    styleUrls: ['./filter.component.scss'],
})
export class Filter implements OnInit {
    @Output() onChange: EventEmitter<any> = new EventEmitter();
    /**
     * ActiveFilters armazena uma nova estrutura
     * apenas com nome do filtro e valor selecionado
     * ex: `category: '241'`
     */
    activeFilters = {
        loadStart: '',
    };

    /**
     * filterList -
     * Lista de filtros para montagem do HTML
     */
    filterList = [];

    filterMobileExpanded: boolean = true;

    subscription: Subscription;

    constructor(@Inject(WINDOW) private window: Window, private cd: ChangeDetectorRef) {}

    ngOnInit(): void {
        this.toggleMenuBasedOnScreenSize();
        this.subscription = this.debouncedResizeListener().subscribe(() => {
            this.toggleMenuBasedOnScreenSize();
        });
    }

    @Input('filters') set filter(filters: Array<unknown>) {
        if (!filters) return; //filters é undefined antes do recebimento da api

        const filtersList = filters.flatMap((filter) => {
            const [key, value] = Object.entries(filter).flat();
            return {
                ...value,
                key,
            };
        });

        // se filterList já existir
        if (this.filterList.length) {
            // preciso deletar os filtros que serão substituidos
            for (const newFilter of filtersList) {
                const upComingKey = newFilter.key;

                // Procuro no array qual o index do objeto
                // que tem a key igual ao upComingKey
                const filterIndexToDelete = this.filterList.findIndex((filterFromList) => {
                    return filterFromList.key === upComingKey;
                });

                //Agora eu removo ele (pra substituir com o que vem da api)
                this.filterList.splice(filterIndexToDelete, 1);
            }

            /* prettier-ignore */
            this.filterList = [
                ...this.filterList, // como eu só deletei o que veio de novo, eu salvo de novo os filtros anteriores ao loadStart
                ...filtersList // esse aqui é o filtro novo
            ];
        } else {
            this.filterList = filtersList;
        }

        /* Salva os filtros ativos */
        this.activeFilters = this.storeActiveFilters(this.filterList);
    }

    private storeActiveFilters(filterList) {
        if (!filterList) {
            throw Error('Necessario passar os filtros da api para o componente filter');
        }

        const activeFromAPI = filterList.reduce((acc, filter) => {
            const key = filter.key;
            const list = filter.list;
            if (!list) return acc;
            if (filter.type === 2) {
                // type {2: checkbox }

                // procura itens selecionados e atribui ao nome do filtro
                // ex: region: [3, 8], sendo 3 e 8 ids de regiões selecionadas
                const arrOfCheckboxValues = this.getCheckboxValues(filter);

                acc[key] = arrOfCheckboxValues;
            } else {
                const selectedRadio = filter.list.find((radio) => radio.selected);
                acc[key] = selectedRadio?.id || '';
            }

            return acc;
        }, {});

        /* Salva filtros selecionados pela api e deixa loadStart vazio, inicialmente */
        return {
            ...this.activeFilters,
            ...activeFromAPI,
        };
    }

    getCheckboxValues(filter: any) {
        const selectedCheckbox = filter.list.filter((checkbox) => checkbox.selected);
        const arrOfCheckboxValues = selectedCheckbox.reduce((acc, cur) => [...acc, cur.id], []);
        return arrOfCheckboxValues;
    }

    /* A key é dinamica, preciso descobrir qual é essa key para acessa-la */
    getOptionsByUnkownKey(filter) {
        const key = this.getKey(filter);
        /* Retorna lista de opções */
        return filter[key].list;
    }

    getKey(filter) {
        // Retorno ex: Objeto 1: [ranking_model], Objeto2: [rating_date];
        const [key] = Object.keys(filter);
        return key;
    }

    onFilterChange({ value, loadStart, type, checked = null }) {
        // Se for tipo checkbox
        if (type === 2) {
            /** Modifica lista para determinar quem está selecionado */
            const filterGroup = this.filterList.find((filter) => filter.key === loadStart);
            const optionIndexToSelect = filterGroup.list.findIndex((option) => option.id === value);
            if (checked) {
                filterGroup.list[optionIndexToSelect].selected = 1;
            } else {
                filterGroup.list[optionIndexToSelect].selected = 0;
            }

            // Persiste itens selecionados activeFilters como array de valores
            // para enviar à API
            const arrOfCheckboxValues = this.getCheckboxValues(filterGroup);
            this.activeFilters[loadStart] = arrOfCheckboxValues;
        }
        // Se for radio, basta substituir o valor
        if (type === 1) {
            this.activeFilters[loadStart] = value;

            const filterGroup = this.filterList.find((filter) => filter.key === loadStart);

            filterGroup.list = filterGroup.list.map((option) => ({ ...option, selected: option.id === value }));
        }

        this.activeFilters.loadStart = loadStart;

        this.onChange.emit({ ...this.activeFilters });

        this.toggleMenuBasedOnScreenSize();
    }

    /**
     * Retorna quantidade de grupos de filtros que possuem seleção
     * Cada grupo selecionado soma um item, independente de quantas opções forem selecionadas
     */
    get activeFiltersLength() {
        return Math.max(
            Object.entries(this.activeFilters).filter(([key, value]) => {
                if (key === 'loadStart') return false;

                return Array.isArray(value) ? value.length : !!value;
            }).length,
            0
        );
    }

    /**
     * debouncedResizeListener - adiciona escuta de resize da tela
     * com debounce
     * @return {Observable}
     */
    debouncedResizeListener(): Observable<any> {
        return fromEvent(this.window, 'resize').pipe(debounce(() => interval(1500)));
    }

    /**
     * toggleMenuBasedOnScreenSize - muda comportamento do hero
     * de acordo com tamanho da tela
     * @return void
     */
    toggleMenuBasedOnScreenSize() {
        //prettier-ignore
        if (this.window.innerWidth > 992) {
            this.filterMobileExpanded = true;
        } else {
            // this.filterMobileExpanded = false;
        }
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
}
